import { useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Reveal } from "@/components/Reveal";
import { BookOpen, ArrowUpRight, Download, ChevronLeft, ChevronRight, Maximize2 } from "lucide-react";

export const GUIDES = [
  { slug: "born-a-crime", title: "Born a Crime", author: "Trevor Noah", genre: "Memoir", level: "Units 3–4 English / EAL", pages: 19 },
  { slug: "we-have-always-lived-in-the-castle", title: "We Have Always Lived in the Castle", author: "Shirley Jackson", genre: "Gothic Novel", level: "Units 3–4 English", pages: 18 },
  { slug: "rainbows-end", title: "Rainbow's End", author: "Jane Harrison", genre: "Play", level: "Units 3–4 English / EAL", pages: 13 },
  { slug: "oedipus-the-king", title: "Oedipus the King", author: "Sophocles", genre: "Greek Tragedy", level: "Units 3–4 English", pages: 32 },
  { slug: "the-memory-police", title: "The Memory Police", author: "Yoko Ogawa", genre: "Speculative Fiction", level: "Units 3–4 English", pages: 37 },
  { slug: "high-ground", title: "High Ground", author: "Stephen Maxwell Johnson", genre: "Film", level: "Units 3–4 English", pages: 23 },
];

export const StudyGuides = () => {
  const [openIndex, setOpenIndex] = useState(null);
  const guide = openIndex === null ? null : GUIDES[openIndex];

  const step = (dir) => setOpenIndex((i) => (i + dir + GUIDES.length) % GUIDES.length);

  return (
    <section className="bg-white dark:bg-[#0f1117]" data-testid="study-guides-section">
      {/* Purple banner header */}
      <div className="bg-gradient-to-br from-brand-purple to-[#4a1e66] px-6 py-16">
        <Reveal className="max-w-3xl mx-auto text-center">
          <Badge className="bg-brand-gold text-white mb-4 font-body font-semibold px-4 py-1.5 rounded-full">
            <BookOpen className="inline h-3.5 w-3.5 mr-1.5 -mt-0.5" /> A peek at some of our work
          </Badge>
          <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-4 leading-tight">
            The guides our students actually study from
          </h2>
          <p className="text-white/80 font-body text-base md:text-lg leading-relaxed">
            A look inside the in-depth study guides we build in-house — the kind of resource that turns a good essay
            into a top-scoring one. Click any guide to read through it.
          </p>
        </Reveal>
      </div>

      <div className="max-w-6xl mx-auto px-6 py-16 -mt-8">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {GUIDES.map((g, i) => (
            <Reveal key={g.slug} delay={(i % 3) * 100}>
              <button
                type="button"
                onClick={() => setOpenIndex(i)}
                className="group block w-full text-left h-full bg-white dark:bg-[#1a1b23] rounded-2xl border border-slate-100 dark:border-slate-800 overflow-hidden card-hover"
                data-testid={`guide-card-${g.slug}`}
              >
                <div className="relative bg-gradient-to-br from-brand-purple/10 to-brand-gold/10 dark:from-brand-purple/20 dark:to-brand-gold/10 py-6 flex justify-center overflow-hidden">
                  <img
                    src={`/guides/${g.slug}.jpg`}
                    alt={`${g.title} VCE English study guide — first page`}
                    loading="lazy"
                    className="w-[62%] rounded-md shadow-lg ring-1 ring-black/5 transition-transform duration-500 group-hover:-translate-y-1.5 group-hover:shadow-xl"
                  />
                  <span className="absolute top-3 right-3 w-8 h-8 rounded-full bg-white/90 dark:bg-[#1a1b23]/90 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                    <ArrowUpRight className="h-4 w-4 text-brand-purple" />
                  </span>
                </div>
                <div className="p-6">
                  <h3 className="font-heading text-lg font-bold text-brand-purple dark:text-brand-purple-light leading-snug">
                    {g.title}
                  </h3>
                  <p className="text-sm text-slate-500 dark:text-slate-400 font-body mt-0.5">{g.author}</p>
                  <div className="flex flex-wrap gap-1.5 mt-3">
                    <Badge className="bg-brand-gold/10 text-brand-gold font-body text-[11px] rounded-full font-semibold">{g.level}</Badge>
                    <Badge className="bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 font-body text-[11px] rounded-full">{g.genre}</Badge>
                    <Badge className="bg-brand-purple-soft dark:bg-brand-purple/20 text-brand-purple dark:text-brand-purple-light font-body text-[11px] rounded-full">{g.pages} pages</Badge>
                  </div>
                </div>
              </button>
            </Reveal>
          ))}
        </div>
      </div>

      {/* In-site PDF viewer */}
      <Dialog open={openIndex !== null} onOpenChange={(o) => !o && setOpenIndex(null)}>
        <DialogContent
          className="max-w-6xl w-[95vw] h-[92vh] grid-rows-[1fr] p-0 gap-0 overflow-hidden bg-white dark:bg-[#1a1b23] border-brand-purple/20 [&>button]:text-white [&>button]:opacity-80 [&>button]:hover:opacity-100 [&>button]:z-20"
          data-testid="guide-viewer-modal"
        >
          {guide && (
            <div className="flex flex-col h-full min-h-0 overflow-hidden">
              {/* Branded header */}
              <div className="flex items-center justify-between gap-4 px-5 py-4 bg-gradient-to-r from-brand-purple to-[#4a1e66]">
                <div className="min-w-0">
                  <DialogTitle className="font-heading text-base md:text-lg font-bold text-white truncate" data-testid="guide-viewer-title">
                    {guide.title}
                  </DialogTitle>
                  <p className="font-body text-xs text-white/70 truncate">
                    {guide.author} · {guide.level} · {guide.pages} pages
                  </p>
                </div>
                <div className="flex items-center gap-1.5 shrink-0 pr-7">
                  <Button variant="ghost" size="icon" onClick={() => step(-1)} title="Previous guide"
                    className="text-white/80 hover:text-white hover:bg-white/10 rounded-full" data-testid="guide-viewer-prev">
                    <ChevronLeft className="h-5 w-5" />
                  </Button>
                  <Button variant="ghost" size="icon" onClick={() => step(1)} title="Next guide"
                    className="text-white/80 hover:text-white hover:bg-white/10 rounded-full" data-testid="guide-viewer-next">
                    <ChevronRight className="h-5 w-5" />
                  </Button>
                  <a href={`/guides/${guide.slug}.pdf`} target="_blank" rel="noreferrer" title="Open the PDF in a new tab"
                    className="hidden sm:inline-flex items-center justify-center h-10 w-10 rounded-full text-white/80 hover:text-white hover:bg-white/10 transition-colors" data-testid="guide-viewer-fullscreen">
                    <Maximize2 className="h-4 w-4" />
                  </a>
                  <a href={`/guides/${guide.slug}.pdf`} download title="Download PDF"
                    className="inline-flex items-center justify-center h-10 w-10 rounded-full text-white/80 hover:text-white hover:bg-white/10 transition-colors" data-testid="guide-viewer-download">
                    <Download className="h-4 w-4" />
                  </a>
                </div>
              </div>

              {/* Pages rendered as images so the viewer works in every browser, including mobile */}
              <div className="flex-1 min-h-0 bg-slate-100 dark:bg-[#0f1117] overflow-y-auto overscroll-contain [-webkit-overflow-scrolling:touch] [touch-action:pan-y]" data-testid="guide-viewer-pages">
                <div className="max-w-3xl mx-auto py-6 px-4 space-y-5">
                  {Array.from({ length: guide.pages }, (_, p) => {
                    const n = String(p + 1).padStart(2, "0");
                    return (
                      <figure key={n} className="relative">
                        <img
                          src={`/guides/pages/${guide.slug}-${n}.jpg`}
                          alt={`${guide.title} study guide — page ${p + 1}`}
                          loading={p < 2 ? "eager" : "lazy"}
                          className="w-full rounded-lg shadow-md ring-1 ring-black/5 bg-white"
                          data-testid={`guide-page-${p + 1}`}
                        />
                        <figcaption className="absolute bottom-2 right-3 rounded-full bg-brand-purple/85 text-white text-[11px] font-body px-2.5 py-0.5">
                          {p + 1} / {guide.pages}
                        </figcaption>
                      </figure>
                    );
                  })}
                </div>
              </div>

              {/* Footer CTA */}
              <div className="flex flex-col sm:flex-row items-center justify-between gap-3 px-5 py-3 border-t border-slate-100 dark:border-slate-800 bg-white dark:bg-[#1a1b23]">
                <p className="font-body text-xs text-slate-500 dark:text-slate-400 text-center sm:text-left">
                  Every student gets guides like this for their own texts.
                </p>
                <a href="/enrol" className="shrink-0">
                  <Button size="sm" className="rounded-full bg-brand-gold hover:bg-brand-gold-hover text-white font-body font-semibold px-5" data-testid="guide-viewer-cta">
                    Enrol now
                  </Button>
                </a>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </section>
  );
};

export default StudyGuides;
